import * as FullStory from "@fullstory/browser";
import { AnalyticsEvent, LoanOfficerDetail, StepResponse } from "pages/application/applicationService";

let isFullStoryShutdown = false;

const isFullStoryEnabled = () => globalThis.env.ENABLE_FULL_STORY && !isFullStoryShutdown;

/** Module-level set — shared across all E-Sign tracking code for the session lifetime.
 *  Prevents double-firing when multiple components/hooks attempt to track the same event.
 *  Survives component remounts and ensures the event fires exactly once per package per session. */
export const sessionESignEventKeys = new Set<string>();

const initFullStory = () => {
  isFullStoryShutdown = false;
  if (!globalThis.env.ENABLE_FULL_STORY) return;
  FullStory.init({ orgId: globalThis.env.FULLSTORY_ORG_ID });
};

/**
 * Immediately disables Full Story data capture
 */
export const shutdownFullStory = () => {
  isFullStoryShutdown = true;
  FullStory.shutdown();
};

/**
 * @param stepCode internal code for the step
 * @param stepLabel String name for StepCode
 * @param skipped true if user skip
 */
export function trackFSStepCompleted(stepCode: string, stepLabel: string, skipped: boolean) {
  if (!isFullStoryEnabled()) return;
  FullStory.event("Step completed", {
    StepCode: stepCode,
    StepName: stepLabel,
    Skipped: skipped ? "Yes" : "No",
  });
}

export function trackFSApplicationStarted(props: {
  leadProvider?: string;
  loanType?: string;
  pathSelected?: string;
  leadCampaign?: string;
  applicationStartedDate: string;
  loanOfficerAssigned?: string;
  borrowerType?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, loanType, pathSelected, leadCampaign, loanOfficerAssigned, applicationStartedDate, borrowerType } = props;

  FullStory.event("Application Started", {
    StartedDate: applicationStartedDate,
    LeadProvider: leadProvider ?? "Unknown",
    LeadCampaign: leadCampaign ?? "Unknown",
    LoanType: loanType ?? "Unknown",
    PathSelected: pathSelected ?? "Unknown",
    LoanOfficerAssigned: loanOfficerAssigned ?? "Not Assigned",
    Environment: globalThis.env.ENVIRONMENT ?? "Unknown",
    borrowerType: borrowerType,
  });
}

const trackFSSummaryEvent = (eventName: string, eventPayload: Record<string, unknown>) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, { ...eventPayload });
};

const trackFSSummaryEditEvent = (stepCode: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Summary Page Edited", {
    SummaryPage: stepCode,
  });
};

export const trackFSInitAppSubmittedEvent = () => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Initial App Submitted", {
    DateSubmitted: new Date().toISOString(),
  });
};

const trackFSOptedVoaEvent = (orderDate: Date) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Opted Voa", { VoaOrderDate: orderDate });
};

const trackFSOptedVoiEvent = (orderDate: Date) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Opted VOI", { VoiOrderDate: orderDate });
};

const trackFSLOAssignedEvent = (loanOfficerDetails: LoanOfficerDetail, stepData: StepResponse) => {
  if (!isFullStoryEnabled()) return;
  FullStory.event("Loan Officer Assigned", {
    LoanOfficerName: loanOfficerDetails.fullName,
    LoanOfficerNMLS: loanOfficerDetails.nmlsLicenseNumber,
    StepCode: stepData.stepCodeText,
    Section: stepData.section,
  });
};

export function trackFSVisitedRegistrationPage(props:{
  leadProvider?: string;
  loanType?: string;
  referringUrl?: string;
  salesChannel?: string;
  loFullName?: string;
  loEmail?: string;
  loNmls?: string;
  state?: string;
  loCardVisible?: boolean;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, loanType, referringUrl, salesChannel, loFullName, loEmail, loNmls, state, loCardVisible } = props;

  FullStory.event("Registration Page Visited", {
    LeadProvider: leadProvider ?? "Unknown",
    LoanPurpose: loanType ?? "Unknown",
    ReferringUrl: referringUrl ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    LOFullName: loFullName ?? null,
    LOEmail: loEmail ?? null,
    LONmls: loNmls ?? null,
    State: state ?? "Unknown",
    LOCardVisible: loCardVisible ?? false,
  });
}

export function trackFSRegistrationPagePhoneLO(props: {
  leadProvider?: string;
  salesChannel?: string;
  referringUrl?: string;
  loFullName?: string;
  loEmail?: string;
  loNmls?: string;
  state?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, salesChannel, referringUrl, loFullName, loEmail, loNmls, state } = props;

  FullStory.event("Registration Page Phone LO", {
    LeadProvider: leadProvider ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    ReferringUrl: referringUrl ?? "Unknown",
    LOFullName: loFullName ?? "Unknown",
    LOEmail: loEmail ?? "Unknown",
    LONmls: loNmls ?? "Unknown",
    State: state ?? "Unknown",
  });
}

export function trackFSRegistrationPageEmailLO(props: {
  leadProvider?: string;
  salesChannel?: string;
  referringUrl?: string;
  loFullName?: string;
  loEmail?: string;
  loNmls?: string;
  state?: string;
}) {
  if (!isFullStoryEnabled()) return;

  const { leadProvider, salesChannel, referringUrl, loFullName, loEmail, loNmls, state } = props;

  FullStory.event("Registration Page Email LO", {
    LeadProvider: leadProvider ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    ReferringUrl: referringUrl ?? "Unknown",
    LOFullName: loFullName ?? "Unknown",
    LOEmail: loEmail ?? "Unknown",
    LONmls: loNmls ?? "Unknown",
    State: state ?? "Unknown",
  });
}

const setFSNavigatedToInsuranceCustomEvent = (nhDealApplicationId: string, redirectionUrl: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("NavigatedToInsurance", {
    nhDealApplicationId,
    redirectionUrl,
  });
};

export const trackFSInsuranceBannerLearnMoreClicked = (
  userId?: string,
  nhDealApplicationId?: string,
) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Insurance Banner Learn More Clicked", {
    UserId: userId ?? "Unknown",
    NhDealApplicationId: nhDealApplicationId ?? "Unknown",
  });
};

export const trackFSTaskInsuranceBannerGetStartedClicked = (
  userId?: string,
  nhDealApplicationId?: string,
) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Task Insurance Banner Get Started Clicked", {
    UserId: userId ?? "Unknown",
    NhDealApplicationId: nhDealApplicationId ?? "Unknown",
  });
};

const setFSUserProperties = (email?: string, lastName?: string) => {
  if (isFullStoryEnabled() && email) {
    FullStory.setUserVars({ email });
  }
  if (isFullStoryEnabled() && lastName) {
    FullStory.setUserVars({ lastName });
  }
};

interface AnalyticsSection {
  section: string;
  eventType: AnalyticsEvent;
  displayName: string;
}

export const analyticsSections: AnalyticsSection[] = [
  { section: "Lead", eventType: "FSLeadCompleted", displayName: "Lead Completed" },
  { section: "Profile", eventType: "FSProfileCompleted", displayName: "Profile Completed" },
  { section: "Property", eventType: "FSPropertyCompleted", displayName: "Property Completed" },
  { section: "Credit", eventType: "FSCreditCompleted", displayName: "Credit Completed" },
  { section: "Finances", eventType: "FSFinanceCompleted", displayName: "Finance Completed" },
  { section: "Acknowledgements", eventType: "FSDeclarationsCompleted", displayName: "Declarations Completed" },
];

export const trackFSNpsDisplayed = (props: {
  count: number;
  loanPurpose?: string;
  salesChannel?: string;
}) => {
  if (!isFullStoryEnabled()) return;

  const { count, loanPurpose, salesChannel } = props;

  FullStory.event("NPS Displayed", {
    Count: count,
    LoanPurpose: loanPurpose ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
  });
};

export const trackFSNpsCompleted = (props: { isComplete: boolean }) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("NPS Completed", {
    NPSComplete: props.isComplete,
  });
};

export const trackFSNpsClosed = (props: {
  loanPurpose?: string;
  salesChannel?: string;
  npsScore?: number | null;
  npsFeedback?: string | null;
}) => {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, npsScore, npsFeedback } = props;

  FullStory.event("NPS Closed", {
    LoanPurpose: loanPurpose ?? "Unknown",
    SalesChannel: salesChannel ?? "Unknown",
    NPSScore: typeof npsScore === "number" ? npsScore : null,
    NPSFeedback: npsFeedback ?? "",
  });
};

export const trackFSServicingCtaClicked = (userId?: string, nhDealApplicationId?: string) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Servicing CTA Clicked", {
    UserId: userId ?? "Unknown",
    NhDealApplicationId: nhDealApplicationId ?? "Unknown",
  });
};

export interface NativeAppFullStoryProps {
  username?: string;
  prospectId?: string;
  dealId?: string;
  losLoanNumber?: string;
  losGuid?: string;
  platform?: string;
}

const getClientPlatform = () => {
  if (typeof navigator === "undefined") return "Unknown";
  const userAgentData = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData;
  return userAgentData?.platform || navigator.platform || "Unknown";
};

const trackFSNativeAppEvent = (eventName: string, props?: NativeAppFullStoryProps) => {
  const payload = {
    Username: props?.username ?? "Unknown",
    ProspectID: props?.prospectId ?? "Unknown",
    DealID: props?.dealId ?? "Unknown",
    Timestamp: new Date().toISOString(),
    EventName: eventName,
    LOSLoanNumber: props?.losLoanNumber ?? "Unknown",
    LOSGUID: props?.losGuid ?? "Unknown",
    Platform: props?.platform ?? getClientPlatform(),
  };

  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, payload);
};

export const trackFSNativeAppBannerClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Banner Clicked", props);

export const trackFSNativeAppBannerDismissed = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Banner Dismissed", props);

export const trackFSNativeAppModalDismissed = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Modal Dismissed", props);

export const trackFSNativeAppModalAppStoreClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Modal App Store Clicked", props);

export const trackFSNativeAppModalMaybeLaterClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Modal Maybe Later Clicked", props);

export const trackFSNativeAppAdClicked = (props?: NativeAppFullStoryProps) =>
  trackFSNativeAppEvent("Native App Ad Clicked", props);

export interface UqualFullStoryProps {
  email?: string;
  sessionId?: string | null;
  dealId?: string | null;
  prospectId?: string | null;
  encompassLoanGuid?: string | null;
  encompassLoanNumber?: string | null;
  nhDealApplicationId?: string | null;
  loanPurpose?: string | null;
  milestone?: string | null;
  wasLoAssigned?: boolean;
  borrowerType?: string | null;
}

const trackFSUqualEvent = (eventName: string, props?: UqualFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    Email: props?.email?.trim() || "Unknown",
    SessionID: props?.sessionId ?? "Unknown",
    DealID: props?.dealId ?? null,
    ProspectID: props?.prospectId ?? null,
    Timestamp: new Date().toISOString(),
    EncompassLoanGuid: props?.encompassLoanGuid ?? null,
    EncompassLoanNumber: props?.encompassLoanNumber ?? null,
    HubDealApplicationID: props?.nhDealApplicationId ?? "Unknown",
    LoanPurpose: props?.loanPurpose ?? "Unknown",
    Milestone: props?.milestone ?? "Unknown",
    WasLOAssigned: props?.wasLoAssigned ?? false,
    BorrowerType: props?.borrowerType ?? "Unknown",
  });
};

export const trackFSUqualBannerStartNowClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Start Now in Banner", props);

export const trackFSUqualBannerDismissed = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Dismiss Banner", props);

export const trackFSUqualCardStartNowClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Start Now in Card", props);

export const trackFSUqualRemindMeLaterClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Remind Me Later", props);

export const trackFSUqualContactMeClicked = (props?: UqualFullStoryProps) =>
  trackFSUqualEvent("Contact Me", props);

interface ExitIntentBaseProps {
  loanPurpose?: string;
  salesChannel?: string;
  stepCode?: string;
  stepCodeName?: string;
}

interface ExitIntentWithAnswersProps extends ExitIntentBaseProps {
  question1?: string;
  question2?: string;
}

export const trackFSExitIntentDisplayed = (props: ExitIntentBaseProps) => {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, stepCode, stepCodeName } = props;

  FullStory.event("Exit Intent Displayed", {
    "Loan Purpose": loanPurpose || "Unknown",
    "Sales Channel": salesChannel || "Unknown",
    Stepcode: stepCode || "Unknown",
    "Stepcode Name": stepCodeName || "Unknown",
  });
};

function trackFSExitIntentWithAnswers(
  eventName: "Exit Intent Completed" | "Exit Intent Cancelled",
  props: ExitIntentWithAnswersProps
) {
  if (!isFullStoryEnabled()) return;

  const { loanPurpose, salesChannel, stepCode, stepCodeName, question1, question2 } = props;

  FullStory.event(eventName, {
    "Loan Purpose": loanPurpose || "Unknown",
    "Sales Channel": salesChannel || "Unknown",
    Stepcode: stepCode || "Unknown",
    "Stepcode Name": stepCodeName || "Unknown",
    "Question 1": question1 || "",
    "Question 2": question2 || "",
  });
}

export const trackFSExitIntentCompleted = (props: ExitIntentWithAnswersProps) =>
  trackFSExitIntentWithAnswers("Exit Intent Completed", props);

export const trackFSExitIntentCancelled = (props: ExitIntentWithAnswersProps) =>
  trackFSExitIntentWithAnswers("Exit Intent Cancelled", props);
export const trackFSDocumentsUploaded = (props: {
  username: string;
  timestamp: string;
  prospectId: string;
  dealId: string | null;
  losLoanNumber: string | null;
  losId: string | null;
  numberOfDocumentsUploaded: number;
  loanMilestone: string;
}) => {
  if (!isFullStoryEnabled()) return;

  const { username, timestamp, prospectId, dealId, losLoanNumber, losId, numberOfDocumentsUploaded, loanMilestone } = props;

  FullStory.event("Documents Uploaded", {
    Username: username,
    Timestamp: timestamp,
    ProspectID: prospectId,
    DealID: dealId,
    LOSLoanNumber: losLoanNumber,
    LOSID: losId,
    NumberOfDocumentsUploaded: numberOfDocumentsUploaded,
    LoanMilestone: loanMilestone,
  });
};

interface SelfRegistrationTrackingProps {
  salesChannel?: string;
  loFullName?: string;
  loEmail?: string;
  loNMLS?: string;
  loanPurpose?: string;
  state?: string;
  rejectedReason?: string;
}

function trackFSSelfRegistrationEvent(
  eventName: "Visited Self-Registration" | "Completed Self-Registration" | "Rejected Self-Registration",
  props: SelfRegistrationTrackingProps,
  additionalProps?: Record<string, unknown>
) {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    "Sales Channel": props.salesChannel ?? "Unknown",
    "LO Full Name": props.loFullName ?? "Not Assigned",
    "LO Email": props.loEmail ?? "Not Assigned",
    "LO NMLS": props.loNMLS ?? "Not Assigned",
    "Loan Purpose": props.loanPurpose ?? "Unknown",
    State: props.state ?? "Unknown",
    ...additionalProps,
  });
}

export const trackFSVisitedSelfRegistration = (props: Omit<SelfRegistrationTrackingProps, "rejectedReason">) =>
  trackFSSelfRegistrationEvent("Visited Self-Registration", props);

export const trackFSCompletedSelfRegistration = (props: Omit<SelfRegistrationTrackingProps, "rejectedReason">) =>
  trackFSSelfRegistrationEvent("Completed Self-Registration", props, { Completed: true });

export const trackFSRejectedSelfRegistration = (props: SelfRegistrationTrackingProps) =>
  trackFSSelfRegistrationEvent("Rejected Self-Registration", props, {
    Completed: false,
    "Rejected Reason": props.rejectedReason ?? "Unknown",
  });

type ESignFullStoryProps = {
  username?: string;
  prospectId?: string;
  dealId?: string | null;
  timestamp: string;
  eventName?: string;
  losLoanNumber?: string | null;
  losGuid?: string | null;
  loanMilestone?: string;
};

const E_SIGN_EVENT_NAMES = {
  created: "E-Sign Created",
  started: "E-Sign Started",
  completed: "E-Sign Completed",
} as const;

type ESignEventName = (typeof E_SIGN_EVENT_NAMES)[keyof typeof E_SIGN_EVENT_NAMES];

const trackFSESignEvent = (eventName: ESignEventName, props: ESignFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  FullStory.event(eventName, {
    Username: props.username ?? "Unknown",
    ProspectID: props.prospectId ?? "Unknown",
    DealID: props.dealId ?? "Unknown",
    Timestamp: props.timestamp,
    EventName: props.eventName ?? eventName,
    LOSLoanNumber: props.losLoanNumber ?? "Unknown",
    LOSGUID: props.losGuid ?? "Unknown",
    LoanMilestone: props.loanMilestone ?? "Unknown",
  });
};

export const trackFSESignCreated = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.created, props);

export const trackFSESignStarted = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.started, props);

export const trackFSESignCompleted = (props: ESignFullStoryProps) =>
  trackFSESignEvent(E_SIGN_EVENT_NAMES.completed, props);

export interface PageNavigationFullStoryProps {
  email?: string;
  prospectId?: string;
  sessionId?: string | null;
  sessionStartTimestamp?: string | null;
  pageName: string;
  loanNumber?: string | null;
  loanGuid?: string | null;
  dealId?: string | null;
  loSalesDomain?: string;
}

export const trackFSDashboardPageNavigated = (props: PageNavigationFullStoryProps) => {
  if (!isFullStoryEnabled()) return;

  const { email, prospectId, sessionId, sessionStartTimestamp, pageName, loanNumber, loanGuid, dealId, loSalesDomain } = props;

  FullStory.event("Dashboard Page Navigated", {
    Email: email ?? "Unknown",
    ProspectID: prospectId ?? "Unknown",
    SessionID: sessionId ?? "Unknown",
    SessionStartTimestamp: sessionStartTimestamp ?? "Unknown",
    NavigationEventTimestamp: new Date().toISOString(),
    PageName: pageName,
    LoanNumber: loanNumber ?? null,
    LoanGUID: loanGuid ?? null,
    DealID: dealId ?? null,
    LOSalesDomain: loSalesDomain ?? "Unassigned",
  });
};

export type LanguagePreference = "Spanish" | "English";

export function trackFSLanguagePreferenceChanged(props: {
  fromLanguage: LanguagePreference;
  toLanguage: LanguagePreference;
  applicationStep?: string;
  applicationId?: string | null;
  loanNumber?: string | null;
  changeSource: "user_selector";
  acknowledgmentLanguage?: "spanish" | "english";
}) {
  if (!isFullStoryEnabled()) return;

  FullStory.event("Language Preference Changed", {
    FromLanguage: props.fromLanguage,
    SelectedLanguage: props.toLanguage,
    ApplicationStep: props.applicationStep ?? null,
    ApplicationId: props.applicationId ?? null,
    LoanNumber: props.loanNumber ?? null,
    ChangeSource: props.changeSource,
    AcknowledgmentLanguage: props.acknowledgmentLanguage ?? null,
    Environment: globalThis.env.ENVIRONMENT ?? "Unknown",
  });
}

export {
  initFullStory,
  setFSUserProperties,
  setFSNavigatedToInsuranceCustomEvent,
  trackFSSummaryEvent,
  trackFSSummaryEditEvent,
  trackFSOptedVoaEvent,
  trackFSLOAssignedEvent,
  trackFSOptedVoiEvent,
};
